import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ListingStatusClient, ListingStatusDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ListingStatusClient(API_BASE_URL, axiosInstance);
const MAX_DROPDOWN_PAGE_SIZE = 9999;

// Cached hook for dropdowns - get all active listing statuses
export const useListingStatuses = () => {
  return useQuery({
    queryKey: ['listingStatuses', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchListingStatuses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for list pages
export const useListingStatusSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchListingStatuses(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Get by ID
export const useListingStatusById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['listingStatuses', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await apiClient.getListingStatusById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateListingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ListingStatusDTO) => {
      return await apiClient.createListingStatus(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingStatuses'] });
    },
  });
};

// Update mutation
export const useUpdateListingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ListingStatusDTO }) => {
      return await apiClient.updateListingStatus(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingStatuses'] });
    },
  });
};

// Delete mutation
export const useDeleteListingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteListingStatus(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingStatuses'] });
    },
  });
};